import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { onAlert } from '../../lib/alert-engine';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: 'alerts';
}

const NAV_ITEMS: NavItem[] = [
  {
    path: '/',
    label: 'Spectrum',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>
    ),
  },
  {
    path: '/alerts',
    label: 'Alerts',
    badge: 'alerts',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
      </svg>
    ),
  },
  {
    path: '/devices',
    label: 'Devices',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" />
      </svg>
    ),
  },
  {
    path: '/baseline',
    label: 'Baseline',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
      </svg>
    ),
  },
  {
    path: '/connect',
    label: 'Connect',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
    ),
  },
];

/** Track unseen alert count — resets when user navigates to /alerts */
function useUnseenAlerts() {
  const location = useLocation();
  const [unseen, setUnseen] = useState(0);

  // Subscribe to new alerts
  useEffect(() => {
    return onAlert(() => {
      setUnseen((c) => c + 1);
    });
  }, []);

  // Reset when viewing alerts page
  useEffect(() => {
    if (location.pathname === '/alerts') {
      setUnseen(0);
    }
  }, [location.pathname]);

  return unseen;
}

export default function BottomNav() {
  const location = useLocation();
  const unseenAlerts = useUnseenAlerts();

  return (
    <nav className="flex items-center justify-around border-t border-white/5 bg-scope-bg/95 backdrop-blur-sm safe-bottom flex-shrink-0" aria-label="Main navigation">
      {NAV_ITEMS.map((item) => {
        const isActive = item.path === '/'
          ? location.pathname === '/'
          : location.pathname.startsWith(item.path);

        const badgeCount = item.badge === 'alerts' ? unseenAlerts : 0;

        return (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-0.5 py-2 px-3 transition-colors relative ${
              isActive ? 'text-scope-trace' : 'text-gray-600 active:text-gray-400'
            }`}
            aria-current={isActive ? 'page' : undefined}
          >
            <div className="relative">
              {item.icon}
              {badgeCount > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-severity-crit text-white text-[8px] readout font-bold px-1 leading-none">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </div>
            <span className="text-[9px] readout font-bold">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
